import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HousePlus, LayoutDashboard, LogOut } from "lucide-react";
import { API_URL, BRANCH, LOGOUT_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { AuthContext } from "../../context/AuthContext";
import { cn } from "../../utils/tailwindUtils";
import { setActiveBranch, clearActiveBranch, isBranchAvail } from "../../utils/branchSession";
import appLogo from "../../assets/png/HRZyra-White-Logo.png";

// Org-admin branch selection landing (shown after login when branch_avail=true),
// styled after the eMedHub "Select the Clinic" screen but themed to HRMS: a teal
// (sidebar-primary) brand band on top, teal section headers and card accents. A
// centered Dashboard Overview card, then the branch cards centered below.

// Centered section header with teal divider lines on either side.
const SectionTitle = ({ children }) => (
    <div className="flex items-center justify-center gap-4 my-6">
        <span className="h-px flex-1 max-w-[220px] bg-sidebar-primary/30" />
        <h2 className="text-lg font-semibold text-sidebar-primary whitespace-nowrap">{children}</h2>
        <span className="h-px flex-1 max-w-[220px] bg-sidebar-primary/30" />
    </div>
);

const SelectCard = ({ Icon, label, selected, onClick, badge }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            "group relative w-56 h-36 rounded-xl border overflow-hidden",
            "flex flex-col items-center justify-center gap-3 shadow-sm",
            "transition-all duration-150 cursor-pointer focus:outline-none",
            "focus:ring-2 focus:ring-sidebar-primary/30",
            selected
                ? "bg-sidebar-primary border-sidebar-primary text-sidebar-primary-foreground shadow-md"
                : "bg-card dark:bg-gray-800 border-border dark:border-gray-700 text-foreground dark:text-gray-200 hover:border-sidebar-primary hover:shadow-md"
        )}
    >
        {/* corner badge — e.g. "Main" / "New" */}
        {badge && (
            <span
                className={cn(
                    "absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold",
                    selected
                        ? "bg-white/25 text-sidebar-primary-foreground"
                        : "bg-sidebar-primary/10 text-sidebar-primary"
                )}
            >
                {badge}
            </span>
        )}
        {/* teal top accent bar (hidden when the whole card is teal) */}
        {!selected && <span className="absolute top-0 inset-x-0 h-1 bg-sidebar-primary" />}
        <span
            className={cn(
                "h-14 w-14 rounded-full flex items-center justify-center transition-colors",
                selected ? "bg-white/20" : "bg-sidebar-primary/10 group-hover:bg-sidebar-primary/15"
            )}
        >
            <Icon className={cn("h-7 w-7", selected ? "text-sidebar-primary-foreground" : "text-sidebar-primary")} />
        </span>
        <span className="text-sm font-semibold text-center px-2">{label}</span>
    </button>
);

const BranchSelect = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const { logoutauth } = useContext(AuthContext);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null); // 'dashboard' | branch id

    // Same server-logout + local-clear flow as the in-app header, so this page
    // (which has no sidebar) still offers a way out.
    const handleLogout = async () => {
        try {
            const res = await fetch2Logout();
            if (res?.status === "success") {
                logoutauth();
                navigate("/");
            } else {
                ToastMsg("error", res?.message || "Logout failed");
            }
        } catch (e) {
            ToastMsg("error", e.message);
        }
    };
    const fetch2Logout = async () => {
        const authtoken = sessionStorage.getItem("token");
        const res = await window.fetch(API_URL + LOGOUT_URL, {
            method: "GET",
            headers: { Authorization: `token ${authtoken}` },
        });
        return res.json();
    };

    useEffect(() => {
        if (!isBranchAvail()) {
            navigate("/home", { replace: true });
            return;
        }
        let active = true;
        fetch(BRANCH, { page_no: 1, page_size: 100 })
            .then((res) => {
                if (!active) return;
                const list = res?.data?.result || res?.data || [];
                // Sort by id ascending so the most recently added branch (highest
                // id) appears last in the list.
                const sorted = (Array.isArray(list) ? [...list] : []).sort(
                    (a, b) => Number(a.id) - Number(b.id)
                );
                setBranches(sorted);
            })
            .catch(() => ToastMsg("error", "Could not load branches"))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Dashboard Overview = org-wide (no branch filter): clear active branch and
    // go to the dashboard. A branch card = enter that branch scoped.
    const enterDashboard = () => {
        setSelected("dashboard");
        clearActiveBranch();
        // Marker so the gate lets them through without a specific branch.
        sessionStorage.setItem("active_branch_id", "all");
        navigate("/home");
    };

    const enterBranch = (branch) => {
        setSelected(branch.id);
        setActiveBranch(branch.id, branch.name, !!branch.is_main_branch);
        navigate("/home");
    };

    return (
        <div className="min-h-screen w-full bg-background dark:bg-gray-900 overflow-y-auto">
            {/* HRMS teal brand band with logo + logout (no sidebar on this gate page).
                Full-width band; logo/logout inset from the edges via side padding. */}
            <div className="w-full bg-sidebar-primary text-sidebar-primary-foreground">
                <div className="flex items-center justify-between px-8 md:px-16 py-3">
                    <img src={appLogo} alt="HRZyra" className="h-8" />
                    <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 text-sidebar-primary-foreground hover:bg-white/15"
                        onClick={handleLogout}
                    >
                        <LogOut className="h-4 w-4" />
                        Logout
                    </Button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 py-10">

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
                    </div>
                ) : (
                    <>
                        {/* Dashboard overview card — centered on top */}
                        <SectionTitle>Overview</SectionTitle>
                        <div className="flex justify-center">
                            <SelectCard
                                Icon={LayoutDashboard}
                                label="Dashboard Overview"
                                selected={selected === "dashboard"}
                                onClick={enterDashboard}
                            />
                        </div>

                        {/* Branch cards — centered below */}
                        <SectionTitle>Select the Branch to Continue</SectionTitle>
                        {branches.length === 0 ? (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-10">
                                No branches found. Create one under Settings &gt; Branch.
                            </div>
                        ) : (
                            <div className="flex flex-wrap justify-center gap-5">
                                {branches.map((branch) => (
                                    <SelectCard
                                        key={branch.id}
                                        Icon={HousePlus}
                                        label={branch.name}
                                        selected={selected === branch.id}
                                        onClick={() => enterBranch(branch)}
                                        // Only the main branch is badged. Branches remain
                                        // sorted by id, so the newest is still last.
                                        badge={branch.is_main_branch ? "Main" : null}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BranchSelect;
